import type { ConversationSource } from "../../types";

/**
 * Badge identificando o canal de origem da conversa.
 * Cores via tokens semantic (`--channel-*-fg/bg`). SVGs inline ficam aqui.
 */

interface ChannelBadgeProps {
  source?: ConversationSource | null;
  /** Renderiza só ícone, sem label. Útil quando espaço é apertado. */
  iconOnly?: boolean;
}

interface ChannelDef {
  label: string;
  cls: string;
  Icon: () => preact.JSX.Element;
}

const WhatsAppIcon = () => (
  <svg
    class="channel-badge__icon"
    viewBox="0 0 32 32"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M16 0C7.16 0 0 7.16 0 16c0 2.82.74 5.57 2.15 8L0 32l8.18-2.13C10.61 31.27 13.26 32 16 32c8.84 0 16-7.16 16-16S24.84 0 16 0zm0 29.33c-2.43 0-4.79-.65-6.86-1.88l-.49-.29-5.07 1.32 1.35-4.93-.32-.51A13.31 13.31 0 012.67 16C2.67 8.65 8.65 2.67 16 2.67S29.33 8.65 29.33 16 23.35 29.33 16 29.33zm7.31-9.97c-.4-.2-2.36-1.17-2.73-1.3-.36-.13-.63-.2-.9.2-.27.4-1.03 1.3-1.27 1.57-.23.27-.46.3-.86.1s-1.7-.63-3.24-2c-1.2-1.06-2-2.38-2.24-2.78-.23-.4-.02-.61.18-.81.18-.18.4-.46.6-.7.2-.23.27-.4.4-.66.13-.27.07-.5-.03-.7-.1-.2-.9-2.16-1.23-2.96-.32-.78-.65-.67-.9-.68h-.77c-.27 0-.7.1-1.06.5s-1.4 1.37-1.4 3.33 1.43 3.87 1.63 4.13c.2.27 2.82 4.3 6.83 6.03.96.42 1.7.67 2.28.85.95.3 1.82.26 2.5.16.76-.11 2.36-.96 2.7-1.9.33-.93.33-1.73.23-1.9-.1-.17-.36-.27-.76-.47z" />
  </svg>
);

const InstagramIcon = () => (
  <svg
    class="channel-badge__icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
  >
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
  </svg>
);

const ChatIcon = () => (
  <svg
    class="channel-badge__icon"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const CHANNELS: Record<string, ChannelDef> = {
  whatsapp:  { label: "WhatsApp",  cls: "channel-badge--whatsapp",  Icon: WhatsAppIcon },
  instagram: { label: "Instagram", cls: "channel-badge--instagram", Icon: InstagramIcon },
  talker:    { label: "Talker",    cls: "channel-badge--talker",    Icon: ChatIcon },
  web:       { label: "Web",       cls: "channel-badge--web",       Icon: ChatIcon },
};

const FALLBACK: ChannelDef = {
  label: "Outro",
  cls: "channel-badge--unknown",
  Icon: ChatIcon,
};

export function ChannelBadge({ source, iconOnly }: ChannelBadgeProps) {
  const key = (source || "whatsapp").toLowerCase();
  const def = CHANNELS[key] ?? FALLBACK;
  const { Icon, label, cls } = def;
  return (
    <span class={`channel-badge ${cls}`} aria-label={label}>
      <Icon />
      {!iconOnly && <span>{label}</span>}
    </span>
  );
}
